import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as yup from "yup";
import moment from "moment-timezone";
import axiosInstance from "../utils/axiosConfig";
import { notifyToast } from "../utils/notify";
import { showErrorToast } from "../utils/toastUtils";
import { useSettings } from "../hooks/useSettings";
import { PrimaryButton, SecondaryButton } from "./button";

export const CAB_TYPE_OPTIONS = [
  { value: "COMPACT", label: "Compact" },
  { value: "TAXI", label: "Taxi" },
  { value: "TAXI_EV", label: "Taxi EV" },
  { value: "PLUS", label: "Plus" },
  { value: "EXECUTIVE", label: "Executive" },
  { value: "PEEKUP_BLUE", label: "Peekup Blue" },
  { value: "PEEKUP_BLUE_XL", label: "Peekup Blue XL" },
];

// Cab types the create API accepts (subset of CAB_TYPE_OPTIONS)
const SCHEDULE_CAB_TYPE_OPTIONS = [
  { value: "COMPACT", label: "Compact (Sedan)" },
  { value: "PLUS", label: "Plus (SUV)" },
  { value: "EXECUTIVE", label: "Executive (BMW)" },
];

export const PLATFORM_OPTIONS = [
  { value: "APP", label: "PeekUp" },
  { value: "ELIFE", label: "eLife" },
  { value: "TWELVE_GO", label: "12GO" },
];

const initialValues = {
  source: "TWELVE_GO",
  partner_booking_id: "",
  name: "",
  phone: "",
  email: "",
  date: "",
  time: "",
  pickup_address: "",
  dropoff_address: "",
  cab_type: "",
  amount: "",
  notes: "",
};

// Maps API field names (400 data.error) to form fields
const API_FIELD_TO_FORM_FIELD = {
  scheduled_at: "time",
  pickup_location: "pickup_address",
  dropoff_location: "dropoff_address",
};

const flattenErrorMessage = (value) => {
  if (Array.isArray(value)) return value.map(flattenErrorMessage).join(", ");
  if (value && typeof value === "object") {
    return Object.values(value).map(flattenErrorMessage).join(", ");
  }
  return String(value ?? "");
};

const validationSchema = yup.object({
  source: yup.string().required("Platform is required"),
  partner_booking_id: yup
    .string()
    .trim()
    .required("Partner booking ID is required"),
  name: yup.string().trim().required("Name is required"),
  phone: yup
    .string()
    .trim()
    .matches(/^\+[1-9]\d{7,14}$/, "Enter number with country code, e.g. +639171234567")
    .required("Number is required"),
  email: yup.string().trim().email("Enter a valid email"),
  date: yup.string().required("Date is required"),
  time: yup.string().required("Time is required"),
  pickup_address: yup.string().trim().required("From location is required"),
  dropoff_address: yup.string().trim().required("To location is required"),
  cab_type: yup.string().required("Cab type is required"),
  amount: yup
    .number()
    .typeError("Enter a valid amount")
    .min(0, "Amount cannot be negative")
    .required("Amount is required"),
  notes: yup.string().trim(),
});

const ScheduleRideModal = ({ open, onClose, onSuccess }) => {
  const { timezone } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Date & time are entered in the dashboard timezone (Asia/Manila by default)
      const scheduledAt = moment.tz(
        `${values.date} ${values.time}`,
        "YYYY-MM-DD HH:mm",
        timezone
      );

      if (scheduledAt.isBefore(moment())) {
        formik.setFieldError("time", "Scheduled time must be in the future");
        return;
      }

      const email = values.email.trim();
      const notes = values.notes.trim();
      const payload = {
        source: values.source,
        partner_booking_id: values.partner_booking_id.trim(),
        name: values.name.trim(),
        phone: values.phone.trim(),
        ...(email && { email }),
        scheduled_at: scheduledAt.format(),
        pickup_location: { address_text: values.pickup_address.trim() },
        dropoff_location: { address_text: values.dropoff_address.trim() },
        cab_type: values.cab_type,
        amount: Number(values.amount).toFixed(2),
        ...(notes && { notes }),
      };

      setIsSubmitting(true);
      try {
        await axiosInstance.post("/crm/scheduled-rides/create/", payload);
        notifyToast.success("Success", "Ride scheduled successfully");
        resetForm();
        onSuccess?.();
        onClose();
      } catch (error) {
        const fieldErrors = error?.response?.data?.data?.error;
        if (fieldErrors && typeof fieldErrors === "object") {
          Object.entries(fieldErrors).forEach(([field, message]) => {
            const formField = API_FIELD_TO_FORM_FIELD[field] ?? field;
            if (formField in initialValues) {
              formik.setFieldError(formField, flattenErrorMessage(message));
            }
          });
        }
        showErrorToast(
          error?.response?.data?.message ?? "Failed to schedule ride"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && formik.errors[name],
    size: "small",
    fullWidth: true,
  });

  const handleClose = () => {
    if (isSubmitting) return;
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "Proxima Nova",
          fontWeight: 600,
        }}
      >
        Add Schedule Ride
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Platform"
                required
                {...fieldProps("source")}
              >
                {PLATFORM_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Partner Booking ID"
                required
                placeholder="12GO-123456"
                {...fieldProps("partner_booking_id")}
              />
            </Stack>
            <TextField label="Name" required {...fieldProps("name")} />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Number"
                required
                placeholder="+639171234567"
                {...fieldProps("phone")}
              />
              <TextField
                label="Email (Optional)"
                type="email"
                {...fieldProps("email")}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Date"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: moment().tz(timezone).format("YYYY-MM-DD"),
                }}
                {...fieldProps("date")}
              />
              <TextField
                label="Time"
                type="time"
                required
                InputLabelProps={{ shrink: true }}
                {...fieldProps("time")}
              />
            </Stack>
            <TextField
              label="From Location"
              required
              multiline
              {...fieldProps("pickup_address")}
            />
            <TextField
              label="To Location"
              required
              multiline
              {...fieldProps("dropoff_address")}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Cab Type"
                required
                {...fieldProps("cab_type")}
              >
                {SCHEDULE_CAB_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Amount (₱)"
                type="number"
                required
                inputProps={{ min: 0, step: "0.01" }}
                {...fieldProps("amount")}
              />
            </Stack>
            <TextField
              label="Notes (Optional)"
              multiline
              minRows={2}
              placeholder="Flight no., pax, luggage, etc."
              {...fieldProps("notes")}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <SecondaryButton onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </PrimaryButton>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleRideModal;
